import { defineComponent as _defineComponent } from 'vue'
import { resolveComponent as _resolveComponent, createVNode as _createVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, createElementBlock as _createElementBlock } from "vue"

import {computed, onMounted, ref} from 'vue'
import {useRoute, useRouter} from "vue-router";
import {UserRole, userService} from '@/service/user-service';
import BranchContextDisplay from '@/components/branch/BranchContextDisplay.vue';
import UserDebugInfo from '@/components/debug/UserDebugInfo.vue';
import LanguageSwitcher from '@/components/common/LanguageSwitcher.vue';
import { useI18n } from 'vue-i18n'


export default /*#__PURE__*/_defineComponent({
  __name: 'HomeView',
  setup(__props) {

const router = useRouter()
const route = useRoute()
const leftDrawerOpen = ref(true)
const miniState = ref(false)

const { t } = useI18n()

const loginMenuItem = computed(() => ({title: t('auth.login'), icon: "login", route: "/login"}))

const baseMenuItems = computed(() => [
  {title: t('navigation.sell'), icon: "sell", route: "/sell"},
  {title: t('navigation.addProduct'), icon: "add", route: "/add"},
  {title: t('navigation.allProducts'), icon: "inventory_outline", route: "/all"},
  {title: t('navigation.clients'), icon: "man_2", route: "/clients"},
  {title: t('navigation.organizations'), icon: "corporate_fare", route: "/organizations"},
  {title: t('navigation.orders'), icon: "list_alt", route: "/orders"},
  {title: t('navigation.manageCategories'), icon: "account_tree", route: "/category/manage"},
  {title: t('navigation.unitTypes'), icon: "straighten", route: "/unit-types"},
])

const branchMenuItems = computed(() => [
  {
    title: t('branch.branchManagement'),
    icon: "account_tree",
    route: "/branches",
    requiredRole: UserRole.MARKET,
    allowSeller: true
  },
  {title: t('branch.branchUsers'), icon: "people", route: "/branch-users", requiredRole: UserRole.MANAGER},
])

const adminMenuItems = computed(() => [
  {
    title: t('admin.dashboard'),
    icon: "admin_panel_settings",
    route: "/admin",
    requiredRole: UserRole.ADMIN
  },
  {title: t('admin.userManagement'), icon: "people", route: "/admin/users", requiredRole: UserRole.ADMIN},
  {
    title: t('admin.organizationManagement'),
    icon: "business",
    route: "/admin/organizations",
    requiredRole: UserRole.ADMIN
  },
])

const currentUser = computed(() => userService.getCurrentUser());

const menItems = computed(() => {
  if (!currentUser.value) {
    return [loginMenuItem.value];
  }

  const items = [...baseMenuItems.value];

  // Add branch management items for allowed roles
  branchMenuItems.value.forEach(item => {
    const user = currentUser.value;
    const hasRequiredRole = !item.requiredRole || userService.hasRoleOrHigher(item.requiredRole);
    const sellerOverride = item.allowSeller && user?.role === UserRole.SELLER;
    if (hasRequiredRole || sellerOverride) {
      items.push(item);
    }
  });

  // Add admin items for admins
  adminMenuItems.value.forEach(item => {
    if (!item.requiredRole || userService.hasRoleOrHigher(item.requiredRole)) {
      items.push(item);
    }
  });

  return items;
});

const userDisplayName = computed(() => userService.getUserDisplayName());
const roleDisplayName = computed(() => userService.getRoleDisplayName());
const isAuthenticated = computed(() => !!currentUser.value);
const showSetupTestAdmin = computed(() => {
  if (!isAuthenticated.value) return false;

  const user = currentUser.value;
  if (!user) {
    // Allow button so anonymous sessions can seed an admin user
    return true;
  }

  if (userService.isAdmin()) {
    return false;
  }

  return user.role !== UserRole.SELLER;
});

function logout() {
  userService.logout();
  window.location.href = "/login"
}

function routeTo(path) {
  router.push(path)
}

function isActiveRoute(path) {
  return route.path === path;
}

function setupTestAdmin() {
  // Use your actual user data from the database
  const adminUser = {
    id: 4,
    username: 'monoahror',
    firstName: 'Ahrorjon',
    lastName: 'Komilov',
    email: '',
    role: UserRole.ADMIN,
    organizationId: 2,
    organizationName: 'Your Organization',
    assignedBranches: [],
    isActive: true
  };

  // Keep existing token if available, otherwise use a placeholder
  const existingToken = localStorage.getItem('token') || 'Bearer placeholder-token';

  localStorage.setItem('user', JSON.stringify(adminUser));
  localStorage.setItem('token', existingToken);

  // Reload to refresh all computed values
  window.location.reload();
}

// Try to fetch user info if token exists but no user data
onMounted(async () => {
  const token = localStorage.getItem('token');
  const user = userService.getCurrentUser();

  if (token && !user) {
    console.log('Token exists but no user data, fetching from backend...');
    await userService.fetchCurrentUser();
  }
});

return (_ctx: any,_cache: any) => {
  const _component_q_btn = _resolveComponent("q-btn")!
  const _component_q_toolbar_title = _resolveComponent("q-toolbar-title")!
  const _component_q_space = _resolveComponent("q-space")!
  const _component_q_toolbar = _resolveComponent("q-toolbar")!
  const _component_q_header = _resolveComponent("q-header")!
  const _component_q_avatar = _resolveComponent("q-avatar")!
  const _component_q_item_section = _resolveComponent("q-item-section")!
  const _component_q_item_label = _resolveComponent("q-item-label")!
  const _component_q_item = _resolveComponent("q-item")!
  const _component_q_separator = _resolveComponent("q-separator")!
  const _component_q_icon = _resolveComponent("q-icon")!
  const _component_q_list = _resolveComponent("q-list")!
  const _component_q_scroll_area = _resolveComponent("q-scroll-area")!
  const _component_q_drawer = _resolveComponent("q-drawer")!
  const _component_router_view = _resolveComponent("router-view")!
  const _component_q_page_container = _resolveComponent("q-page-container")!
  const _component_q_layout = _resolveComponent("q-layout")!

  return (_openBlock(), _createBlock(_component_q_layout, {
    view: "lHh LpR fFf",
    class: "bg-mg-100"
  }, {
    default: _withCtx(() => [
      _createVNode(_component_q_header, { class: "bg-mg-900" }, {
        default: _withCtx(() => [
          _createVNode(_component_q_toolbar, null, {
            default: _withCtx(() => [
              _createVNode(_component_q_btn, {
                flat: "",
                dense: "",
                round: "",
                onClick: _cache[0] || (_cache[0] = ($event: any) => (leftDrawerOpen.value = !leftDrawerOpen.value)),
                "aria-label": "Menu",
                icon: "menu"
              }),
              _createVNode(_component_q_toolbar_title, null, {
                default: _withCtx(() => [
                  _createTextVNode(_toDisplayString(_ctx.$t('common.appName')), 1)
                ]),
                _: 1
              }),
              _createVNode(_component_q_space),
              _createVNode(BranchContextDisplay, { class: "q-mr-md" }),
              _createVNode(LanguageSwitcher)
            ]),
            _: 1
          })
        ]),
        _: 1
      }),
      _createVNode(_component_q_drawer, {
        modelValue: leftDrawerOpen.value,
        "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((leftDrawerOpen).value = $event)),
        mini: miniState.value,
        breakpoint: 600,
        "show-if-above": "",
        width: 220,
        class: "bg-green-950 text-white",
        onMouseenter: _cache[2] || (_cache[2] = ($event: any) => (miniState.value = false)),
        onMouseleave: _cache[3] || (_cache[3] = ($event: any) => (miniState.value = false))
      }, {
        default: _withCtx(() => [
          _createVNode(_component_q_scroll_area, { class: "fit" }, {
            default: _withCtx(() => [
              _createVNode(_component_q_list, null, {
                default: _withCtx(() => [
                  (currentUser.value)
                    ? (_openBlock(), _createBlock(_component_q_item, {
                        key: 0,
                        class: "q-pa-md"
                      }, {
                        default: _withCtx(() => [
                          _createVNode(_component_q_item_section, { avatar: "" }, {
                            default: _withCtx(() => [
                              _createVNode(_component_q_avatar, {
                                color: "primary",
                                "text-color": "white",
                                icon: "person"
                              })
                            ]),
                            _: 1
                          }),
                          _createVNode(_component_q_item_section, null, {
                            default: _withCtx(() => [
                              _createVNode(_component_q_item_label, { class: "text-weight-bold" }, {
                                default: _withCtx(() => [
                                  _createTextVNode(_toDisplayString(userDisplayName.value), 1)
                                ]),
                                _: 1
                              }),
                              _createVNode(_component_q_item_label, {
                                caption: "",
                                class: "text-grey-4"
                              }, {
                                default: _withCtx(() => [
                                  _createTextVNode(_toDisplayString(roleDisplayName.value), 1)
                                ]),
                                _: 1
                              })
                            ]),
                            _: 1
                          })
                        ]),
                        _: 1
                      }))
                    : _createCommentVNode("", true),
                  (currentUser.value)
                    ? (_openBlock(), _createBlock(_component_q_separator, {
                        key: 1,
                        class: "q-mb-md"
                      }))
                    : _createCommentVNode("", true),
                  (isAuthenticated.value)
                    ? (_openBlock(), _createBlock(_component_q_item_label, {
                        key: 2,
                        header: ""
                      }, {
                        default: _withCtx(() => [
                          _createTextVNode(_toDisplayString(_ctx.$t('navigation.mainMenu')), 1)
                        ]),
                        _: 1
                      }))
                    : _createCommentVNode("", true),
                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(menItems.value, (item) => {
                    return (_openBlock(), _createBlock(_component_q_item, {
                      clickable: "",
                      key: item.title,
                      active: isActiveRoute(item.route),
                      "active-class": "bg-white text-mg-900 text-weight-bold",
                      onClick: ($event: any) => (routeTo(item.route))
                    }, {
                      default: _withCtx(() => [
                        _createVNode(_component_q_item_section, { avatar: "" }, {
                          default: _withCtx(() => [
                            _createVNode(_component_q_icon, {
                              name: item.icon
                            }, null, 8, ["name"])
                          ]),
                          _: 2
                        }, 1024),
                        _createVNode(_component_q_item_section, null, {
                          default: _withCtx(() => [
                            _createVNode(_component_q_item_label, null, {
                              default: _withCtx(() => [
                                _createTextVNode(_toDisplayString(item.title), 1)
                              ]),
                              _: 2
                            }, 1024)
                          ]),
                          _: 2
                        }, 1024)
                      ]),
                      _: 2
                    }, 1032, ["active", "onClick"]))
                  }), 128)),
                  _createVNode(_component_q_separator, { class: "q-my-md" }),
                  (showSetupTestAdmin.value)
                    ? (_openBlock(), _createBlock(_component_q_item, {
                        key: 3,
                        clickable: "",
                        onClick: setupTestAdmin
                      }, {
                        default: _withCtx(() => [
                          _createVNode(_component_q_item_section, { avatar: "" }, {
                            default: _withCtx(() => [
                              _createVNode(_component_q_icon, { name: "admin_panel_settings" })
                            ]),
                            _: 1
                          }),
                          _createVNode(_component_q_item_section, null, {
                            default: _withCtx(() => [
                              _createVNode(_component_q_item_label, null, {
                                default: _withCtx(() => [
                                  _createTextVNode(_toDisplayString(_ctx.$t('admin.setupTestAdmin')), 1)
                                ]),
                                _: 1
                              })
                            ]),
                            _: 1
                          })
                        ]),
                        _: 1
                      }))
                    : _createCommentVNode("", true),
                  (isAuthenticated.value)
                    ? (_openBlock(), _createBlock(_component_q_item, {
                        key: 4,
                        clickable: "",
                        onClick: logout
                      }, {
                        default: _withCtx(() => [
                          _createVNode(_component_q_item_section, { avatar: "" }, {
                            default: _withCtx(() => [
                              _createVNode(_component_q_icon, { name: "logout" })
                            ]),
                            _: 1
                          }),
                          _createVNode(_component_q_item_section, null, {
                            default: _withCtx(() => [
                              _createVNode(_component_q_item_label, null, {
                                default: _withCtx(() => [
                                  _createTextVNode(_toDisplayString(_ctx.$t('auth.logout')), 1)
                                ]),
                                _: 1
                              })
                            ]),
                            _: 1
                          })
                        ]),
                        _: 1
                      }))
                    : _createCommentVNode("", true)
                ]),
                _: 1
              })
            ]),
            _: 1
          })
        ]),
        _: 1
      }, 8, ["modelValue", "mini"]),
      _createVNode(_component_q_page_container, null, {
        default: _withCtx(() => [
          _createVNode(_component_router_view),
          _createVNode(UserDebugInfo)
        ]),
        _: 1
      })
    ]),
    _: 1
  }))
}
}

})